import { FarmHudLayout } from 'Layouts/Farm/FarmHudLayout';
import BaseUIHandler from 'System/UI/BaseUIHandler';
import { PopupID } from 'State/UI/PopupID';
import BaseUISystem, { UIEvent } from 'System/UI/BaseUISystem';
import RenderSystem, { RenderSystemEvent } from 'System/Render/RenderSystem';
import { GameObjectEvent } from 'GameObject/GameObject';
import Interactable from 'Component/Interactable';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import { IGameState } from 'State/Game/IGameState';
import { Layout } from 'Utility/layout/createLayout';
import TransformComponent from 'Component/Transform';
import PIXIRenderer from 'System/Render/PIXIRenderer';
import SpriteComponent from 'Component/Render/Sprite';
import TextComponent from 'Component/Render/Text';
import { isNative } from 'Utility/Platform';
import Game from 'Game';
import MaskComponent from 'Component/Render/Mask';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import { GameActions } from 'State/Game/GameActions';
import { FarmActions } from 'State/Farm/FarmActions';
import { UIActions } from 'State/UI/UIActions';
import PayloadHelper from 'Utility/SDK/PayloadHelper';
import Renderable from 'Component/Renderable';
import { PlaySound } from 'System/Audio/AudioHelper';
import { GameScene } from 'State/Game/GameScene';
import { FarmStateHelper } from 'State/Player/Farm/FarmStateHelper';
import getVisibleItems = FarmStateHelper.getVisibleItems;
import areAnyItemsUnseen = FarmStateHelper.areAnyItemsUnseen;
import PIXI = require('pixi.js');
import LeaderboardHelper from '../LeaderboardHelper';

type FarmHUDProps = {
    system: BaseUISystem;
};

export class FarmHUDHandler extends BaseUIHandler {
    private layout: any;
    public constructionButton: GameObject;
    public rewardContainer: GameObject;

    constructor(props: FarmHUDProps) {
        super(PopupID.FARM_HUD, props.system, false);

        this.alwaysVisible = true;
    }

    public getLayout(): Layout {
        return this.layout;
    }

    protected onCreate(): void {
        super.onCreate();

        this.layout = new FarmHudLayout();
        this.layout.root.addComponent(RelativePositionComponent, { x: 0.5, y: 0.5 });
        this.element.addChild(this.layout.root);

        this.layout.shareButton.visible = false;

        const nameTextComponent = this.layout.nameText.getComponent(TextComponent);
        nameTextComponent.text = Game.locale.getText('YourFarm');

        // Setup avatar
        const avatarSprite = this.layout.avatar.getComponent(SpriteComponent);
        const setAvatar = () => {
            this.layout.circleMask.container.scale.set(1 / avatarSprite.container.scale.x);
        };
        LeaderboardHelper.setPlayerAvatar('player_avatar', Game.sdk.getAvatar(), avatarSprite, 100, setAvatar);
        this.layout.avatar.addComponent(MaskComponent, { mask: this.layout.circleMask.container });

        this.setupButtons();

        this.system.scene.on(UIEvent.DISABLE_INTERACTIVITY, this.disableInteractivity.bind(this));
        this.system.scene.on(UIEvent.RESTORE_INTERACTIVITY, this.restoreInteractivity.bind(this));

        RenderSystem.on(RenderSystemEvent.RESIZE, this.handleResize);
        this.cleanupManager.add(() => {
            RenderSystem.off(RenderSystemEvent.RESIZE);
        });
    }

    public setupButtons(): void {
        this.layout.editButton.on(GameObjectEvent.SELECTED, this.handleEditClick);
        // this.layout.shareButton.on(GameObjectEvent.SELECTED, this.handleShareClick);
        this.layout.backButton.on(GameObjectEvent.SELECTED, this.handleBackClick);
        [this.layout.editButton, this.layout.backButton].forEach(gameObject => {
            applyDefaultButtonAnimation(gameObject);
            gameObject.getComponent(Interactable).toggleable = true;
        });

        this.constructionButton = this.layout.editButton;
        this.rewardContainer = this.layout.nameText;
    }

    public handleStateChanged(state: IGameState) {
        this.handleResize();
        const prevState = Game.store.getPreviousState();

        const constructionEnabled = state.farmState.constructionModeEnabled;

        const backText = constructionEnabled ? 'Back' : 'BackToMap';
        this.layout.backBtnText.getComponent(TextComponent).text = Game.locale.getText(backText);

        this.layout.editButton.getComponent(SpriteComponent).texture = !constructionEnabled
            ? 'my_farm_build'
            : 'my_farm_build_back';

        if (state.activeScene.result !== GameScene.FARM && prevState.activeScene.result === GameScene.FARM) {
            this.layout.root.getComponentsInChildren(Interactable).forEach((interactable: any) => {
                interactable.enabled = false;
            });
        }

        // Show/hide HUD items while "share" state is active.
        // TODO: Use state.farmState property to manage "share" state instead of state.UIState.
        const showHUDItems = state.UIState.popupID !== PopupID.FARM_SHARE;

        // TODO: Bind visibility directly to state changes/values and memoize.
        this.layout.editButton.getComponent(Renderable).visible = showHUDItems;
        this.layout.backButton.getComponent(Renderable).visible = showHUDItems;

        // TODO: Add 'share' support property to state.
        const shareIsSupported = !isNative();

        this.layout.notification.getComponent(SpriteComponent).visible = areAnyItemsUnseen(
            getVisibleItems(state, false, true).map(item => item.itemId),
            state.playerState.decoItemsSeen
        );

        super.handleStateChanged(state);
    }

    private readonly handleEditClick = (): void => {
        const state = Game.store.getState();

        PlaySound(!state.farmState.constructionModeEnabled ? 'construction_mode_enter' : 'construction_mode_exit');

        FarmActions.setConstructionMode(!state.farmState.constructionModeEnabled);
    };

    private readonly handleShareClick = (): void => {
        PlaySound('share_mode');

        const textureKey = 'farmShareTexture';
        this.layout.backButton.getComponent(Renderable).visible = false;
        this.layout.editButton.getComponent(Renderable).visible = false;
        const baseImageTexture = PayloadHelper.getScreenshot();
        this.layout.backButton.getComponent(Renderable).visible = true;
        this.layout.editButton.getComponent(Renderable).visible = true;
        Game.resource.cacheResource(textureKey, baseImageTexture);
        PIXI.Texture.addToCache(baseImageTexture, textureKey);
        UIActions.showPopup(PopupID.FARM_SHARE, { textureKey });
    };

    private readonly handleBackClick = (): void => {
        const state = Game.store.getState();
        if (state.farmState.constructionModeEnabled) {
            PlaySound('construction_mode_exit');
            FarmActions.setConstructionMode(!state.farmState.constructionModeEnabled);
        } else {
            if (state.activeScene.result === GameScene.FARM) {
                GameActions.loadWorld();
            }
        }
    };

    private readonly handleResize = (): void => {
        if (this.layout === undefined) return;

        const { width, height } = PIXIRenderer.renderer;
        this.layout.topContainer.getComponent(TransformComponent).x = width / 2;
        this.layout.bottomContainer.getComponent(TransformComponent).y = height;
    };
}
